import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Trash2, FileText, FileImage, File, Loader2, FolderOpen, Files,
  Search, Grid3X3, List, Download, Eye, MoreVertical, Calendar,
  Upload, Link2, ExternalLink, Filter, ChevronRight, FolderClosed,
  FileSpreadsheet, FileVideo, FileAudio, HardDrive, Clock, SortAsc,
  SortDesc, ChevronDown, X, Copy, Share2
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { filesApi, eventsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.04 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15, scale: 0.95 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: "easeOut" } }
};

// File type configurations
const fileTypeConfig = {
  rider: { 
    icon: FileText, 
    label: 'Rider', 
    color: '#22C55E',
    bgClass: 'from-green-500/20 to-green-500/5'
  },
  contract: { 
    icon: FileSpreadsheet, 
    label: 'Contrato', 
    color: '#3B82F6',
    bgClass: 'from-blue-500/20 to-blue-500/5'
  },
  budget: { 
    icon: FileText, 
    label: 'Presupuesto', 
    color: '#F59E0B',
    bgClass: 'from-amber-500/20 to-amber-500/5'
  },
  image: { 
    icon: FileImage, 
    label: 'Imagen', 
    color: '#EC4899',
    bgClass: 'from-pink-500/20 to-pink-500/5'
  },
  planning: { 
    icon: Calendar, 
    label: 'Planning', 
    color: '#8B5CF6',
    bgClass: 'from-purple-500/20 to-purple-500/5'
  },
  video: { 
    icon: FileVideo, 
    label: 'Video', 
    color: '#EF4444',
    bgClass: 'from-red-500/20 to-red-500/5'
  },
  audio: { 
    icon: FileAudio, 
    label: 'Audio', 
    color: '#06B6D4',
    bgClass: 'from-cyan-500/20 to-cyan-500/5'
  },
};

// Storage stats card
const StorageCard = ({ totalFiles, typeBreakdown }) => (
  <motion.div 
    variants={itemVariants}
    className="relative bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-zinc-950/80 border border-zinc-800/50 rounded-2xl p-5 backdrop-blur-sm overflow-hidden"
  >
    <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" />
    
    <div className="relative flex items-center gap-4 mb-4">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 flex items-center justify-center">
        <HardDrive size={22} className="text-[#D4AF37]" />
      </div>
      <div>
        <p className="text-2xl font-black text-white">{totalFiles}</p>
        <p className="text-xs text-zinc-500 uppercase tracking-wider">Archivos totales</p>
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-2">
      {Object.entries(typeBreakdown).slice(0, 3).map(([type, count]) => {
        const config = fileTypeConfig[type] || fileTypeConfig.planning;
        return (
          <div key={type} className="text-center p-2 rounded-lg bg-zinc-800/30">
            <p className="text-sm font-bold" style={{ color: config.color }}>{count}</p>
            <p className="text-[10px] text-zinc-500 uppercase">{config.label}</p>
          </div>
        );
      })}
    </div>
  </motion.div>
);

// File Card Component - Grid View
const FileCard = ({ file, events, onDelete, onCopyUrl }) => {
  const config = fileTypeConfig[file.file_type] || { icon: File, label: file.file_type, color: '#71717A', bgClass: 'from-zinc-500/20 to-zinc-500/5' };
  const Icon = config.icon;
  const eventName = file.event_id ? events.find(e => e.id === file.event_id)?.title : null;

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ scale: 1.02, y: -4 }}
      className="group relative bg-gradient-to-br from-zinc-900/90 via-zinc-900/70 to-zinc-950/90 border border-zinc-800/60 rounded-2xl overflow-hidden backdrop-blur-sm hover:border-zinc-700 transition-all duration-300"
    >
      {/* Top accent line */}
      <div 
        className="absolute top-0 left-0 right-0 h-0.5 opacity-60 group-hover:opacity-100 transition-opacity"
        style={{ background: `linear-gradient(90deg, transparent, ${config.color}, transparent)` }}
      />
      
      {/* File preview area */}
      <div className={`relative h-32 bg-gradient-to-br ${config.bgClass} flex items-center justify-center overflow-hidden`}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzI3MjcyYSIgb3BhY2l0eT0iMC4yIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
        
        <div className="relative">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3"
            style={{ 
              background: `linear-gradient(135deg, ${config.color}30, ${config.color}10)`,
              boxShadow: `0 8px 32px ${config.color}20`
            }}
          >
            <Icon size={28} style={{ color: config.color }} />
          </div>
        </div>

        {/* Quick actions overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <Eye size={18} className="text-white" />
          </a>
          <button
            onClick={() => onCopyUrl(file.url)}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <Copy size={18} className="text-white" />
          </button>
          <a
            href={file.url}
            download
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <Download size={18} className="text-white" />
          </a>
        </div>
      </div>

      {/* File info */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-white text-sm line-clamp-1 flex-1">{file.name}</h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-7 h-7 rounded-lg hover:bg-zinc-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreVertical size={14} className="text-zinc-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
              <DropdownMenuItem asChild>
                <a href={file.url} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                  <Eye size={14} className="mr-2" /> Ver archivo
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onCopyUrl(file.url)}>
                <Copy size={14} className="mr-2" /> Copiar enlace
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={() => onDelete(file.id)} className="text-red-400 focus:text-red-400">
                <Trash2 size={14} className="mr-2" /> Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <span 
            className="text-[10px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wider"
            style={{ 
              backgroundColor: `${config.color}15`,
              color: config.color
            }}
          >
            {config.label}
          </span>
          {eventName && (
            <span className="text-[10px] text-zinc-500 truncate max-w-[100px]">
              {eventName}
            </span>
          )}
        </div>

        {file.description && (
          <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{file.description}</p>
        )}

        <div className="flex items-center justify-between text-[10px] text-zinc-600 pt-3 border-t border-zinc-800/50">
          <span className="flex items-center gap-1">
            <Clock size={10} />
            {format(new Date(file.created_at), 'd MMM yyyy', { locale: es })}
          </span>
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-[#D4AF37] hover:text-[#E5C04B] transition-colors"
          >
            Abrir <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

// File Row Component - List View
const FileRow = ({ file, events, onDelete, onCopyUrl }) => {
  const config = fileTypeConfig[file.file_type] || { icon: File, label: file.file_type, color: '#71717A' };
  const Icon = config.icon;
  const eventName = file.event_id ? events.find(e => e.id === file.event_id)?.title : null;

  return (
    <motion.div
      variants={itemVariants}
      className="group flex items-center gap-4 p-3 rounded-xl hover:bg-zinc-800/30 transition-colors"
    >
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ 
          background: `linear-gradient(135deg, ${config.color}20, ${config.color}05)`,
        }}
      >
        <Icon size={18} style={{ color: config.color }} />
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-white text-sm truncate">{file.name}</h3>
        <p className="text-xs text-zinc-500 truncate">
          {config.label} {eventName && `• ${eventName}`}
        </p>
      </div>

      <div className="hidden md:block text-xs text-zinc-500 w-24">
        {format(new Date(file.created_at), 'd MMM yyyy', { locale: es })}
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 rounded-lg hover:bg-zinc-700 flex items-center justify-center transition-colors"
        >
          <Eye size={14} className="text-zinc-400" />
        </a>
        <button
          onClick={() => onCopyUrl(file.url)}
          className="w-8 h-8 rounded-lg hover:bg-zinc-700 flex items-center justify-center transition-colors"
        >
          <Copy size={14} className="text-zinc-400" />
        </button>
        <button
          onClick={() => onDelete(file.id)}
          className="w-8 h-8 rounded-lg hover:bg-red-500/20 flex items-center justify-center transition-colors"
        >
          <Trash2 size={14} className="text-red-400" />
        </button>
      </div>
    </motion.div>
  );
};

// Folder/Group Component
const FileGroup = ({ title, files, events, viewMode, onDelete, onCopyUrl, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <motion.div variants={itemVariants} className="mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 w-full text-left mb-3 group"
      >
        <div className="w-8 h-8 rounded-lg bg-zinc-800/50 flex items-center justify-center group-hover:bg-zinc-800 transition-colors">
          {isOpen ? <FolderOpen size={16} className="text-[#D4AF37]" /> : <FolderClosed size={16} className="text-zinc-500" />}
        </div>
        <span className="text-sm font-semibold text-zinc-300 group-hover:text-white transition-colors">{title}</span>
        <span className="text-xs text-zinc-600 bg-zinc-800/50 px-2 py-0.5 rounded-full">{files.length}</span>
        <ChevronRight 
          size={16} 
          className={`text-zinc-600 transition-transform ml-auto ${isOpen ? 'rotate-90' : ''}`} 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pl-11">
                {files.map((file) => (
                  <FileCard 
                    key={file.id} 
                    file={file} 
                    events={events} 
                    onDelete={onDelete}
                    onCopyUrl={onCopyUrl}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1 pl-11">
                {files.map((file) => (
                  <FileRow 
                    key={file.id} 
                    file={file} 
                    events={events} 
                    onDelete={onDelete}
                    onCopyUrl={onCopyUrl}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FilesPage = () => {
  const { hasPermission } = useAuth();
  const [files, setFiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState({ eventId: 'all', fileType: 'all' });
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [formData, setFormData] = useState({
    name: '',
    file_type: 'rider',
    url: '',
    description: '',
    event_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [filesRes, eventsRes] = await Promise.all([
        filesApi.getAll(
          filter.eventId !== 'all' ? filter.eventId : null,
          filter.fileType !== 'all' ? filter.fileType : null
        ),
        eventsApi.getAll()
      ]);
      setFiles(filesRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar archivos');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort files
  const processedFiles = files
    .filter(file => 
      file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return 0;
    });

  // Group files by event
  const filesByEvent = processedFiles.reduce((acc, file) => {
    const eventId = file.event_id || 'general';
    if (!acc[eventId]) acc[eventId] = [];
    acc[eventId].push(file);
    return acc;
  }, {});

  // Type breakdown for stats
  const typeBreakdown = files.reduce((acc, file) => {
    acc[file.file_type] = (acc[file.file_type] || 0) + 1;
    return acc;
  }, {});

  const getEventName = (eventId) => {
    if (eventId === 'general') return 'Archivos Generales';
    const event = events.find(e => e.id === eventId);
    return event?.title || 'Evento Desconocido';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        event_id: formData.event_id || null
      };
      await filesApi.create(payload);
      toast.success('Archivo agregado correctamente');
      setModalOpen(false);
      setFormData({ name: '', file_type: 'rider', url: '', description: '', event_id: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating file:', error);
      toast.error('Error al agregar archivo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await filesApi.delete(fileId);
      toast.success('Archivo eliminado');
      fetchData();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error al eliminar archivo');
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado al portapapeles');
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 space-y-6"
          data-testid="files-manager"
        >
          {/* Header */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
          >
            <motion.div variants={itemVariants}>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 flex items-center justify-center">
                  <Files size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white">
                    Archivos
                  </h1>
                  <p className="text-zinc-500 text-sm">
                    Gestiona riders, contratos y documentos
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Button 
                onClick={() => setModalOpen(true)}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B8962D] hover:from-[#E5C04B] hover:to-[#D4AF37] text-black font-semibold shadow-lg shadow-[#D4AF37]/20"
                data-testid="add-file-btn"
              >
                <Plus size={18} className="mr-2" />
                Nuevo Archivo
              </Button>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <StorageCard totalFiles={files.length} typeBreakdown={typeBreakdown} />
            
            {/* Quick stats */}
            {[
              { label: 'Contratos', type: 'contract', color: '#3B82F6' },
              { label: 'Riders', type: 'rider', color: '#22C55E' },
              { label: 'Otros', type: 'other', color: '#8B5CF6' }
            ].map((stat, i) => (
              <motion.div 
                key={stat.type}
                variants={itemVariants}
                className="relative bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-zinc-950/80 border border-zinc-800/50 rounded-2xl p-4 backdrop-blur-sm overflow-hidden"
              >
                <div 
                  className="absolute -top-8 -right-8 w-20 h-20 rounded-full blur-2xl opacity-30"
                  style={{ backgroundColor: stat.color }}
                />
                <div className="relative flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}05)` }}
                  >
                    {stat.type === 'contract' && <FileSpreadsheet size={18} style={{ color: stat.color }} />}
                    {stat.type === 'rider' && <FileText size={18} style={{ color: stat.color }} />}
                    {stat.type === 'other' && <File size={18} style={{ color: stat.color }} />}
                  </div>
                  <div>
                    <p className="text-xl font-bold text-white">
                      {stat.type === 'other' 
                        ? files.filter(f => !['contract', 'rider'].includes(f.file_type)).length
                        : typeBreakdown[stat.type] || 0}
                    </p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">{stat.label}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Toolbar */}
          <motion.div 
            variants={itemVariants}
            initial="hidden"
            animate="show"
            className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between bg-zinc-900/50 border border-zinc-800/50 rounded-2xl p-4"
          >
            {/* Search */}
            <div className="relative flex-1 max-w-md w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar archivos..."
                className="pl-10 bg-zinc-800/50 border-zinc-700/50 focus:border-[#D4AF37]/50 rounded-xl"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Filters & View Toggle */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select 
                value={filter.fileType} 
                onValueChange={(value) => setFilter(prev => ({ ...prev, fileType: value }))}
              >
                <SelectTrigger className="w-36 bg-zinc-800/50 border-zinc-700/50 rounded-xl">
                  <Filter size={14} className="mr-2 text-zinc-500" />
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="rider">Riders</SelectItem>
                  <SelectItem value="contract">Contratos</SelectItem>
                  <SelectItem value="budget">Presupuestos</SelectItem>
                  <SelectItem value="image">Imágenes</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                </SelectContent>
              </Select>

              <Select 
                value={sortBy} 
                onValueChange={setSortBy}
              >
                <SelectTrigger className="w-36 bg-zinc-800/50 border-zinc-700/50 rounded-xl">
                  <SortAsc size={14} className="mr-2 text-zinc-500" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="newest">Más recientes</SelectItem>
                  <SelectItem value="oldest">Más antiguos</SelectItem>
                  <SelectItem value="name">Nombre A-Z</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center bg-zinc-800/50 rounded-xl p-1 border border-zinc-700/50">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  <Grid3X3 size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Files Content */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-800/50 rounded-2xl overflow-hidden">
                  <div className="h-32 bg-zinc-800/50 animate-pulse" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-zinc-800/50 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-zinc-800/50 rounded animate-pulse w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : processedFiles.length === 0 ? (
            <motion.div 
              variants={itemVariants}
              initial="hidden"
              animate="show"
              className="text-center py-20 bg-zinc-900/30 border border-zinc-800/30 rounded-2xl"
            >
              <div className="w-20 h-20 rounded-2xl bg-zinc-800/50 flex items-center justify-center mx-auto mb-6">
                <FolderOpen size={40} className="text-zinc-600" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {searchTerm ? 'Sin resultados' : 'Sin archivos'}
              </h3>
              <p className="text-zinc-500 mb-6 max-w-md mx-auto">
                {searchTerm 
                  ? `No se encontraron archivos para "${searchTerm}"`
                  : 'Agrega tu primer archivo para comenzar a organizar tus documentos'}
              </p>
              {!searchTerm && (
                <Button 
                  onClick={() => setModalOpen(true)} 
                  className="bg-gradient-to-r from-[#D4AF37] to-[#B8962D] hover:from-[#E5C04B] hover:to-[#D4AF37] text-black font-semibold"
                >
                  <Plus size={18} className="mr-2" />
                  Agregar Archivo
                </Button>
              )}
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              {filter.eventId === 'all' ? (
                // Grouped by event
                Object.entries(filesByEvent).map(([eventId, eventFiles]) => (
                  <FileGroup
                    key={eventId}
                    title={getEventName(eventId)}
                    files={eventFiles}
                    events={events}
                    viewMode={viewMode}
                    onDelete={handleDelete}
                    onCopyUrl={handleCopyUrl}
                  />
                ))
              ) : (
                // Flat view when filtered by event
                viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {processedFiles.map((file) => (
                      <FileCard 
                        key={file.id} 
                        file={file} 
                        events={events} 
                        onDelete={handleDelete}
                        onCopyUrl={handleCopyUrl}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1 bg-zinc-900/30 border border-zinc-800/30 rounded-2xl p-3">
                    {processedFiles.map((file) => (
                      <FileRow 
                        key={file.id} 
                        file={file} 
                        events={events} 
                        onDelete={handleDelete}
                        onCopyUrl={handleCopyUrl}
                      />
                    ))}
                  </div>
                )
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Add File Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="bg-zinc-900 border border-zinc-800 max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 flex items-center justify-center">
                  <Upload size={18} className="text-[#D4AF37]" />
                </div>
                Nuevo Archivo
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
                  Nombre del archivo
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Rider DJ Martinez"
                  required
                  className="bg-zinc-800/50 border-zinc-700 focus:border-[#D4AF37]/50 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
                    Tipo
                  </Label>
                  <Select 
                    value={formData.file_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, file_type: value }))}
                  >
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 focus:border-[#D4AF37]/50 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="rider">Rider</SelectItem>
                      <SelectItem value="contract">Contrato</SelectItem>
                      <SelectItem value="budget">Presupuesto</SelectItem>
                      <SelectItem value="image">Imagen</SelectItem>
                      <SelectItem value="planning">Planning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
                    Evento
                  </Label>
                  <Select 
                    value={formData.event_id || 'none'} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, event_id: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 focus:border-[#D4AF37]/50 rounded-xl">
                      <SelectValue placeholder="Sin evento" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none">General</SelectItem>
                      {events.map(event => (
                        <SelectItem key={event.id} value={event.id}>{event.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
                  URL del archivo
                </Label>
                <div className="relative">
                  <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    value={formData.url}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://..."
                    required
                    className="pl-9 bg-zinc-800/50 border-zinc-700 focus:border-[#D4AF37]/50 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
                  Descripción (opcional)
                </Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Breve descripción del archivo"
                  className="bg-zinc-800/50 border-zinc-700 focus:border-[#D4AF37]/50 rounded-xl"
                />
              </div>

              <DialogFooter className="pt-4 gap-2">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => setModalOpen(false)} 
                  className="hover:bg-zinc-800 rounded-xl"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting} 
                  className="bg-gradient-to-r from-[#D4AF37] to-[#B8962D] hover:from-[#E5C04B] hover:to-[#D4AF37] text-black font-semibold rounded-xl"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Plus size={16} className="mr-2" />
                      Agregar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default FilesPage;
